import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ContextButton from 'components/ContextButton';
import componentStyles from 'styles/partials/_filterbase.css';
import btnStyles from 'styles/partials/_button.css';
import { PrimaryButton, SecondaryButton } from '../connectedForm/Button';
import styled from 'styled-components';
import { serviceActions, serviceTypes } from '../../state/modules/service';

export default class FilterBase extends Component {
  constructor(props) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.getStandardDescription = this.getStandardDescription.bind(this);
    this.updateFilter = this.updateFilter.bind(this);
    this.updateFilterData = this.updateFilterData.bind(this);
    this.onClose = this.onClose.bind(this);
    this.handleContextClose = this.handleContextClose.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.dismissFilter = this.dismissFilter.bind(this);
    this.checkValueRange = this.checkValueRange.bind(this);

    let initialValues = this.getInitialValues(props);
    this.committedValues = initialValues;
    this.state = { filterValues: initialValues };

    this.styles = {};
    Object.assign(this.styles, componentStyles, btnStyles);
    this.inputEl = null;
    this.setInputRef = (input) => {
      this.inputEl = input;
    };
    this.filterRef = React.createRef();
  }

  componentDidMount() {
    // Update filter data when created (even if filter has no conditions) so that container knows filter is created
    this.updateFilterData();
    if (this.inputEl) this.inputEl.focus();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // Keep sql filter text in step with conditions (may switch view to a filter on same field and maybe even
    // with the same value, FiltersContainer clears down sql filters when view is switched so need to set them back up)
    if (this.props.currentViewName !== prevProps.currentViewName) {
      let initialValues = this.getInitialValues(this.props);
      this.committedValues = initialValues;
      this.setState({ filterValues: initialValues }, () => this.updateFilterData());
    }
  }

  // Default methods with no implementation to be overridden in derived class
  getBadgeText() {}
  getDescription() {}
  getQueryFilters() {}
  checkValid() {}
  renderPopout() {}

  getInitialValues(props) {
    let initialValues = Object.assign({}, props);

    delete initialValues.label;
    delete initialValues.type;
    delete initialValues.fieldName;
    delete initialValues.onFilterUpdated;
    delete initialValues.position;
    delete initialValues.dispatch;
    delete initialValues.list;
    delete initialValues.initiallyOpen;

    return initialValues;
  }

  handleChange(id, value) {
    let filterValues = Object.assign({}, this.state.filterValues, {
      [id]: value,
    });
    this.setState({ filterValues: filterValues });
  }

  getStandardDescription(keys) {
    let values = [];
    keys.forEach((key) => {
      if (this.committedValues[key]) {
        values.push(this.committedValues[key]);
      }
    });
    if (!values.length) {
      values.push('All');
    }
    return values.join(', ');
  }

  updateFilter(e) {
    let message = this.checkValid();
    if (message) {
      this.props.dispatch?.(serviceActions.genericAlert('Selection is invalid', message, AlertType.Warning));
      return;
    }

    this.updateFilterData();
    this.onClose(e);
  }

  updateFilterData() {
    this.committedValues = Object.assign({}, this.state.filterValues);
    let filter = Object.assign({}, this.state.filterValues);
    filter.label = this.props.label;
    filter.type = this.props.type;
    filter.fieldName = this.props.fieldName;
    filter.position = this.props.position;
    filter.fieldColumn = this.props.fieldColumn;
    this.props.onFilterUpdated(filter, this.getQueryFilters());
  }

  onClose(e) {
    e.preventDefault();
    this.filterRef.current.popoutClose();
    if (this.handleFilterClosed) {
      this.handleFilterClosed();
    }
  }

  dismissFilter() {
    this.committedValues = Object.assign({}, this.state.filterValues);
    let filter = Object.assign({}, this.state.filterValues);
    filter.label = this.props.label;
    filter.type = this.props.type;
    filter.fieldName = this.props.fieldName;
    filter.position = this.props.position;
    this.props.dismissFilter(filter);
  }

  handleContextClose() {
    this.setState({ filterValues: Object.assign({}, this.committedValues) });
  }

  handleClickOutside() {
    // If click is flagged as being actually part of this component (e.g. calendar popover of date) then
    // return false to prevent context button being closed
    if (this.isInside) {
      this.isInside = false;
      return false;
    }

    // Otherwise context button will close so perform exit handling
    if (this.handleFilterClosed) {
      this.handleFilterClosed();
    }
    return true;
  }

  checkValueRange() {
    return checkValueRange(this.state.filterValues);
  }

  render() {
    let popoutContent = this.renderPopout();
    let badgeText = this.getBadgeText();
    let badge = badgeText ? <span className={this.styles['btnBadge']}>{badgeText}</span> : null;
    let description = (
      <span>
        {this.props.label + ': '}
        {badge} {this.getDescription()}
      </span>
    );

    // Only set dismissFilter if a there is a props.dismissFilter to call
    let dismissFilter = null;
    if (this.props.dismissFilter) {
      dismissFilter = this.dismissFilter;
    }

    let dropdownClass =
      this.props.type === 'filterEnums' ||
      this.props.type === 'filterObjects' ||
      this.props.type === 'filterAttributeObjects' ||
      this.props.type === 'RECORD'
        ? 'filterDropdown'
        : 'dropdown';
    if (this.props.type === 'filterDates') dropdownClass = 'filterDates';
    return (
      <div className={this.styles['container'] + ' ignore-react-onclickoutside'}>
        <ContextButton
          ref={this.filterRef}
          buttonClass="btnBorder"
          buttonLabelClass="btnLabelFilter"
          dropdownClass={dropdownClass}
          label={description}
          tabIndex={this.props.tabIndex}
          onClose={this.handleContextClose}
          handleClickOutside={this.handleClickOutside}
          dismissFilter={null}
          initiallyOpen={this.props.initiallyOpen}
          testid={`${this.props.testid}_context_button`}
        >
          {dropdownClass !== 'filterDropdown' ? popoutContent : null}
          <div className={this.styles['filterBtnContainer'] + ' ignore-react-onclickoutside'}>
            <PrimaryButton
              small
              onClick={this.updateFilter}
              data-testid={`${this.props.testid}_update`}
              style={{ flexGrow: 0.33 }}
            >
              Update
            </PrimaryButton>
            <SecondaryButton small onClick={this.onClose} style={{ flexGrow: 0.33 }}>
              Close
            </SecondaryButton>
            {this.props.hideRemoveButton ? null : (
              <SubtleWarningButton small style={{ flexGrow: 0.33 }} onClick={this.dismissFilter}>
                Remove
              </SubtleWarningButton>
            )}
          </div>
          {dropdownClass === 'filterDropdown' ? popoutContent : null}
        </ContextButton>
      </div>
    );
  }
}

FilterBase.propTypes = {
  label: PropTypes.string,
  type: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  currentViewName: PropTypes.string.isRequired,
  onFilterUpdated: PropTypes.func.isRequired,
  initiallyOpen: PropTypes.bool,
};

export const SubtleWarningButton = styled(SecondaryButton)`
  border-color: orange;
  &:hover,
  &:focus {
    background-color: #ff5252;
  }
`;

export const checkValueRange = (filterValues) => {
  let messages = undefined;
  if (filterValues.from && filterValues.to) {
    if (+filterValues.to < +filterValues.from) {
      messages = [{ message: 'To value is less than From value' }];
    }
  }
  return messages;
};

export class FilterBaseComposition extends Component {
  constructor(props) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.getStandardDescription = this.getStandardDescription.bind(this);
    this.updateFilter = this.updateFilter.bind(this);
    this.updateFilterData = this.updateFilterData.bind(this);
    this.onClose = this.onClose.bind(this);
    this.handleContextClose = this.handleContextClose.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.dismissFilter = this.dismissFilter.bind(this);

    let initialValues = this.getInitialValues(props);
    this.committedValues = initialValues;
    this.state = { filterValues: initialValues };

    this.styles = {};
    Object.assign(this.styles, componentStyles, btnStyles);
    this.inputEl = null;
    this.setInputRef = (input) => {
      this.inputEl = input;
    };
    this.filterRef = React.createRef();
  }

  componentDidMount() {
    // Update filter data when created (even if filter has no conditions) so that container knows filter is created
    this.updateFilterData();
    if (this.inputEl) this.inputEl.focus();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // Keep sql filter text in step with conditions (may switch view to a filter on same field and maybe even
    // with the same value, FiltersContainer clears down sql filters when view is switched so need to set them back up)
    if (
      this.props.currentViewName !== prevProps.currentViewName &&
      this.props.getDescription(this.committedValues) !== 'All'
    ) {
      let initialValues = this.getInitialValues(this.props);
      this.committedValues = initialValues;
      this.setState({ filterValues: initialValues }, () => this.updateFilterData());
    }
  }

  getInitialValues(props) {
    if (props.getInitialValues) return props.getInitialValues(props);

    let initialValues = Object.assign({}, props);

    if (props.type === 'filterBigDecimals' && !props.filterType) {
      initialValues.filterType = 'RANGE';
    }

    delete initialValues.label;
    delete initialValues.type;
    delete initialValues.fieldName;
    delete initialValues.onFilterUpdated;
    delete initialValues.position;
    delete initialValues.dispatch;
    delete initialValues.list;
    delete initialValues.initiallyOpen;

    return initialValues;
  }

  handleChange(id, value) {
    let filterValues = Object.assign({}, this.state.filterValues, {
      [id]: value,
    });
    this.setState({ filterValues: filterValues });
  }

  getStandardDescription(keys) {
    let values = [];
    keys.forEach((key) => {
      if (this.committedValues[key]) {
        values.push(this.committedValues[key]);
      }
    });
    if (!values.length) {
      values.push('All');
    }
    return values.join(', ');
  }

  updateFilter(e) {
    let message = this.props.checkValid();
    if (message) {
      this.props.dispatch?.(serviceActions.genericAlert('Selection is invalid', message, AlertType.Warning));
      return;
    }

    this.updateFilterData();
    this.onClose(e);
  }

  updateFilterData() {
    this.committedValues = Object.assign({}, this.state.filterValues);
    let filter = Object.assign({}, this.state.filterValues);
    filter.label = this.props.label;
    filter.type = this.props.type;
    filter.fieldName = this.props.fieldName;
    filter.position = this.props.position;
    filter.fieldColumn = this.props.fieldColumn;
    this.props.onFilterUpdated(filter, this.props.getQueryFilters(this.committedValues, this.state.filterValues));
  }

  onClose(e) {
    e.preventDefault();
    this.filterRef.current.popoutClose();
    if (this.handleFilterClosed) {
      this.handleFilterClosed();
    }
  }

  dismissFilter() {
    this.committedValues = Object.assign({}, this.state.filterValues);
    let filter = Object.assign({}, this.state.filterValues);
    filter.label = this.props.label;
    filter.type = this.props.type;
    filter.fieldName = this.props.fieldName;
    filter.position = this.props.position;
    this.props.dismissFilter(filter);
  }

  handleContextClose() {
    this.setState({ filterValues: Object.assign({}, this.committedValues) });
  }

  handleClickOutside() {
    // If click is flagged as being actually part of this component (e.g. calendar popover of date) then
    // return false to prevent context button being closed
    if (this.isInside) {
      this.isInside = false;
      return false;
    }

    // Otherwise context button will close so perform exit handling
    if (this.handleFilterClosed) {
      this.handleFilterClosed();
    }
    return true;
  }

  render() {
    let popoutContent = this.props.renderPopout(this.setInputRef, this.state.filterValues, this.handleChange);
    let badgeText = this.props.getBadgeText(this.committedValues);
    let badge = badgeText ? <span className={this.styles['btnBadge']}>{badgeText}</span> : null;
    let description = (
      <span>
        {this.props.label + ': '}
        {badge} {this.props.getDescription(this.committedValues, this.state.filterValues)}
      </span>
    );

    // Only set dismissFilter if a there is a props.dismissFilter to call
    let dismissFilter = null;
    if (this.props.dismissFilter) {
      dismissFilter = this.dismissFilter;
    }

    let dropdownClass =
      this.props.type === 'filterEnums' ||
      this.props.type === 'filterObjects' ||
      this.props.type === 'filterAttributeObjects' ||
      this.props.type === 'RECORD'
        ? 'filterDropdown'
        : 'dropdown';
    if (this.props.type === 'filterDates') dropdownClass = 'filterDates';
    return (
      <div className={this.styles['container'] + ' ignore-react-onclickoutside'}>
        <ContextButton
          ref={this.filterRef}
          buttonClass="btnBorder"
          buttonLabelClass="btnLabelFilter"
          dropdownClass={dropdownClass}
          label={description}
          tabIndex={this.props.tabIndex}
          onClose={this.handleContextClose}
          handleClickOutside={this.handleClickOutside}
          dismissFilter={null}
          initiallyOpen={this.props.initiallyOpen}
          testid={`${this.props.testid}_context_button`}
        >
          {dropdownClass !== 'filterDropdown' ? popoutContent : null}
          <div className={this.styles['filterBtnContainer'] + ' ignore-react-onclickoutside'}>
            <PrimaryButton
              small
              onClick={this.updateFilter}
              data-testid={`${this.props.testid}_update`}
              style={{ flexGrow: 0.33 }}
            >
              Update
            </PrimaryButton>
            <SecondaryButton small onClick={this.onClose} style={{ flexGrow: 0.33 }}>
              Close
            </SecondaryButton>
            {this.props.hideRemoveButton ? null : (
              <SubtleWarningButton small style={{ flexGrow: 0.33 }} onClick={this.dismissFilter}>
                Remove
              </SubtleWarningButton>
            )}
          </div>
          {dropdownClass === 'filterDropdown' ? popoutContent : null}
        </ContextButton>
      </div>
    );
  }
}

FilterBaseComposition.defaultProps = {
  getBadgeText: () => {},
  getDescription: () => {},
  getQueryFilters: () => {},
  renderPopout: () => {},
  checkValid: () => {},
};
